import { capitalize } from "@common/helpers/string";
import { ReportSettingSource } from "@common/helpers/report-settings/report-setting-source";
import { layoutValuesSchema } from "@common/schemas/domains/reportSettings/layout-setting.schema";
import { numberFormatValuesSchema } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    reportSettingsActivityMetadataSchema,
    reportTemplateActivityMetadataSchema,
    type ReportTemplateActivityMetadata,
} from "@common/schemas/domains/reportSettings/report-settings-activity.schema";
import { HistoryDetailsItem } from "@common/types/auditLog";
import { REPORT_SETTINGS_DEFAULT_REPORT_KEY } from "@common/types/reports/report-settings-report-key";
import { APIProcessName } from "@common/constants/process-names";
import {
    APPLIED_TEMPLATE_SOURCE_LABELS,
    type AppliedTemplateSource,
} from "@components/settings/ReportTemplates/apply/applyReportTemplateRows";
import {
    describeResetValue,
    FIELD_NOUNS,
    reportLabel,
    resetValueToText,
    type ResetValuePart,
} from "@components/settings/ReportSettings/reportSettingsSourceCopy";
import { HistoryDetailsBaseRow } from "../cells/historyDetailsBaseRow";

const reportSettingValueSchemas = {
    ...layoutValuesSchema.shape,
    ...numberFormatValuesSchema.shape,
};

const displayableReportSettingsActivityMetadataSchema =
    reportSettingsActivityMetadataSchema.refine(({ changes }) =>
        changes.every((change) => {
            const valueSchema = reportSettingValueSchemas[change.setting];
            return (
                valueSchema.safeParse(change.previous.value).success &&
                valueSchema.safeParse(change.next.value).success
            );
        }),
    );

export interface ReportSettingsUpdateActivityRow extends HistoryDetailsBaseRow {
    id: string;
    report: string | null;
    setting: string | null;
    previous: string | ResetValuePart[] | null;
    modified: string | ResetValuePart[] | null;
    previousSource: string | null;
    modifiedSource: string | null;
}

export interface ReportTemplateActivityValue {
    name: string;
    source?: AppliedTemplateSource;
}

export interface ReportTemplateActivityRow extends HistoryDetailsBaseRow {
    id: string;
    previous: ReportTemplateActivityValue | null;
    modified: ReportTemplateActivityValue | null;
}

export type ReportSettingsActivityRow =
    | ReportSettingsUpdateActivityRow
    | ReportTemplateActivityRow;

export enum ReportSettingsActivityTableKind {
    SETTINGS = "settings",
    TEMPLATE = "template",
}

export const getReportSettingsActivityTableKind = (
    sourceType: string,
): ReportSettingsActivityTableKind =>
    sourceType === APIProcessName.REPORT_SETTINGS_UPDATE
        ? ReportSettingsActivityTableKind.SETTINGS
        : ReportSettingsActivityTableKind.TEMPLATE;

const appliedTemplateSource = (
    kind: "entity-template" | "workspace-template" | "firm-default-template",
): AppliedTemplateSource => {
    switch (kind) {
        case "entity-template":
            return "entity";
        case "workspace-template":
            return "client";
        case "firm-default-template":
            return "firm";
    }
};

const SOURCE_LABELS: Record<ReportSettingSource["kind"], string> = {
    "entity-override": "Entity override",
    "workspace-override": "Client override",
    "entity-template": APPLIED_TEMPLATE_SOURCE_LABELS.entity,
    "workspace-template": APPLIED_TEMPLATE_SOURCE_LABELS.client,
    "firm-default-template": APPLIED_TEMPLATE_SOURCE_LABELS.firm,
    template: "Template override",
    "kick-default": "Kick default",
};

const sourceDisplay = ({
    source,
    spotReportKey,
}: {
    source: ReportSettingSource;
    spotReportKey: ReportSettingSource["reportKey"];
}): string => {
    const templateName =
        source.kind === "entity-template" ||
        source.kind === "workspace-template" ||
        source.kind === "firm-default-template"
            ? (source.templateName ?? "a template")
            : undefined;
    const report =
        source.reportKey !== spotReportKey
            ? reportLabel(source.reportKey)
            : undefined;
    return [SOURCE_LABELS[source.kind], templateName, report]
        .filter(Boolean)
        .join(" · ");
};

const buildSettingsRows = (
    details: HistoryDetailsItem[],
): ReportSettingsUpdateActivityRow[] =>
    details.flatMap<ReportSettingsUpdateActivityRow>((detail) => {
        const parsed =
            displayableReportSettingsActivityMetadataSchema.safeParse(
                detail.metadata?.activity,
            );
        if (!parsed.success) {
            return [
                {
                    id: detail.id,
                    date: detail.createdAt,
                    logDate: detail.createdAt,
                    entityId: detail.entityId,
                    report: null,
                    setting: null,
                    previous: null,
                    modified: null,
                    previousSource: null,
                    modifiedSource: null,
                },
            ];
        }
        const activity = parsed.data;
        return activity.changes.map((change, index) => {
            const value = (
                side: typeof change.previous,
            ): string | ResetValuePart[] => {
                const isBlankText =
                    typeof side.value === "string" && side.value.trim() === "";
                const isDefaultRowVisibilityUnset =
                    change.setting === "rowVisibility" &&
                    change.reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY &&
                    side.source.kind === "kick-default";
                if (isBlankText || isDefaultRowVisibilityUnset) {
                    return "";
                }
                const parts = describeResetValue({
                    field: change.setting,
                    value: side.value,
                });
                for (const part of parts) {
                    if (part.kind === "variable") {
                        return parts;
                    }
                }
                return resetValueToText(parts);
            };
            return {
                id: `${detail.id}-${index}`,
                date: detail.createdAt,
                logDate: detail.createdAt,
                entityId: activity.scope === "entity" ? detail.entityId : null,
                report: reportLabel(change.reportKey),
                setting: capitalize(FIELD_NOUNS[change.setting]),
                previous: value(change.previous),
                modified: value(change.next),
                previousSource: sourceDisplay({
                    source: change.previous.source,
                    spotReportKey: change.reportKey,
                }),
                modifiedSource: sourceDisplay({
                    source: change.next.source,
                    spotReportKey: change.reportKey,
                }),
            };
        });
    });

const templateValue = (
    source: ReportTemplateActivityMetadata["previous"],
): ReportTemplateActivityValue => ({
    name: source.templateName ?? "a template",
    source:
        source.kind === "entity-template" ||
        source.kind === "workspace-template" ||
        source.kind === "firm-default-template"
            ? appliedTemplateSource(source.kind)
            : undefined,
});

const buildTemplateRows = (
    details: HistoryDetailsItem[],
): ReportTemplateActivityRow[] =>
    details.map((detail) => {
        const parsed = reportTemplateActivityMetadataSchema.safeParse(
            detail.metadata?.activity,
        );
        if (!parsed.success) {
            return {
                id: detail.id,
                date: detail.createdAt,
                logDate: detail.createdAt,
                entityId: detail.entityId,
                previous: null,
                modified: null,
            };
        }
        return {
            id: detail.id,
            date: detail.createdAt,
            logDate: detail.createdAt,
            entityId: parsed.data.scope === "entity" ? detail.entityId : null,
            previous: templateValue(parsed.data.previous),
            modified: templateValue(parsed.data.next),
        };
    });

export const buildReportSettingsActivityRows = ({
    sourceType,
    details,
}: {
    sourceType: string;
    details: HistoryDetailsItem[];
}): ReportSettingsActivityRow[] =>
    getReportSettingsActivityTableKind(sourceType) ===
    ReportSettingsActivityTableKind.SETTINGS
        ? buildSettingsRows(details)
        : buildTemplateRows(details);
